import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useToggle } from "../../../hooks/state";
import AppointmentChangeExpirationModal from "./AppointmentChangeExpirationModal";

interface ContextValue {
  showChangeExpirationModal: (
    userUuid: string,
    appointmentUuid: string,
    expiresAt: string,
    options: Options,
  ) => void;
}

interface AppointmentChangeExpirationProviderProps {
  children?: ReactNode;
}

interface Options {
  onSuccess?: VoidFunction;
}

const Context = createContext<ContextValue | undefined>(undefined);

function AppointmentChangeExpirationProvider(
  props: AppointmentChangeExpirationProviderProps,
) {
  const { children } = props;

  const [modalVisible, modalActions] = useToggle(false);

  const [userUuid, setUserUuid] = useState<string>();

  const [appointmentUuid, setAppointmentUuid] = useState<string>();

  const [expiresAt, setExpiresAt] = useState<string>();

  const [options, setOptions] = useState<Options>({});

  const handleSuccess = useCallback(() => {
    options.onSuccess?.();
    modalActions.off();
  }, [options, modalActions]);

  const showChangeExpirationModal = useCallback(
    (
      userUuid: string,
      appointmentUuid: string,
      expiresAt: string,
      options: Options = {},
    ) => {
      setUserUuid(userUuid);
      setAppointmentUuid(appointmentUuid);
      setExpiresAt(expiresAt);
      setOptions(options);
      modalActions.on();
    },
    [modalActions],
  );

  const contextValue = useMemo(() => {
    return { showChangeExpirationModal };
  }, [showChangeExpirationModal]);

  return (
    <Context.Provider value={contextValue}>
      {children}
      <AppointmentChangeExpirationModal
        visible={modalVisible}
        userUuid={userUuid}
        appointmentUuid={appointmentUuid}
        expiresAt={expiresAt}
        onClose={modalActions.off}
        onSuccess={handleSuccess}
      />
    </Context.Provider>
  );
}

export function useAppointmentChangeExpiration() {
  const contextValue = useContext(Context);
  if (!contextValue) {
    throw new Error(
      "useAppointmentChangeExpiration must to be used inside an AppointmentChangeExpirationProvider",
    );
  }
  return contextValue;
}

export default AppointmentChangeExpirationProvider;
